import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PublicBrowseShell from '../../components/public/PublicBrowseShell';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Button from '../../components/ui/Button';
import { tournamentService } from '../../services/tournamentService';
import { isCompetitionPubliclyVisible } from '../../utils/publicCompetitionVisibility';

const formatDate = (isoDate) => {
  if (!isoDate) return 'N/A';
  return new Date(isoDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const statusBadgeClass = (status) => {
  if (status === 'active') return 'bg-success/10 text-success';
  if (status === 'upcoming') return 'bg-primary/10 text-primary';
  if (status === 'cancelled') return 'bg-destructive/10 text-destructive';
  return 'bg-muted text-muted-foreground';
};

const TournamentDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadTournament();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadTournament = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await tournamentService.getTournamentById(id);
      if (!isCompetitionPubliclyVisible(data)) {
        setError('This tournament is no longer available on the public portal.');
        setTournament(null);
        return;
      }
      setTournament(data);
      document.title = `${data?.name || 'Tournament'} - Tanzania Basketball Federation`;
    } catch (err) {
      setError(err.message || 'Failed to load tournament');
    } finally {
      setIsLoading(false);
    }
  };

  const teamsByGroup = React.useMemo(() => {
    if (!tournament) return [];
    const groups = tournament.groups || [];
    const teams = tournament.teams || [];
    const grouped = groups.map((group) => ({
      group,
      teams: teams.filter((t) => t?.groupId === group.id),
    }));
    const ungrouped = teams.filter((t) => !t?.groupId);
    if (ungrouped.length > 0) {
      grouped.push({ group: { id: 'ungrouped', name: 'Unassigned Teams' }, teams: ungrouped });
    }
    return grouped;
  }, [tournament]);

  return (
    <PublicBrowseShell>
      <Button variant="ghost" size="sm" iconName="ArrowLeft" onClick={() => navigate('/tournaments')} className="mb-6">
        Back to Tournaments
      </Button>

      {isLoading ? (
        <div className="bg-card rounded-lg border border-border p-12 card-shadow text-center">
          <Icon name="Loader2" size={40} className="text-primary mx-auto mb-4 animate-spin" />
          <p className="text-muted-foreground">Loading tournament...</p>
        </div>
      ) : error ? (
        <div className="bg-destructive/10 border border-destructive/20 text-destructive rounded-lg p-6">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} />
            <p className="font-medium">{error}</p>
          </div>
        </div>
      ) : !tournament ? (
        <div className="bg-card rounded-lg border border-border p-12 card-shadow text-center">
          <Icon name="Award" size={40} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Tournament not found.</p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-card rounded-lg border border-border card-shadow p-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-lg bg-amber-600/10 flex items-center justify-center shrink-0">
                  <Icon name="Award" size={26} className="text-amber-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">{tournament?.name}</h1>
                  <p className="text-muted-foreground">{tournament?.season}</p>
                </div>
              </div>
              <span className={`text-xs px-3 py-1 rounded-full capitalize ${statusBadgeClass(tournament?.tournamentStatus)}`}>
                {tournament?.tournamentStatus || 'upcoming'}
              </span>
            </div>
            <p className="text-muted-foreground mb-4">{tournament?.description || 'No description available.'}</p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div className="bg-muted/30 rounded-lg p-3">
                <Icon name="CalendarDays" size={20} className="text-primary mx-auto mb-1" />
                <p className="text-xs text-muted-foreground">Start Date</p>
                <p className="font-medium text-foreground">{formatDate(tournament?.startDate)}</p>
              </div>
              <div className="bg-muted/30 rounded-lg p-3">
                <Icon name="CalendarX" size={20} className="text-destructive mx-auto mb-1" />
                <p className="text-xs text-muted-foreground">End Date</p>
                <p className="font-medium text-foreground">{formatDate(tournament?.endDate)}</p>
              </div>
              <div className="bg-muted/30 rounded-lg p-3">
                <Icon name="Users" size={20} className="text-accent mx-auto mb-1" />
                <p className="text-xs text-muted-foreground">Teams</p>
                <p className="font-medium text-foreground">{tournament?.teams?.length ?? 0} / {tournament?.maxTeams ?? 0}</p>
              </div>
              <div className="bg-muted/30 rounded-lg p-3">
                <Icon name="Layers" size={20} className="text-success mx-auto mb-1" />
                <p className="text-xs text-muted-foreground">Groups</p>
                <p className="font-medium text-foreground">{tournament?.groups?.length ?? 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-lg border border-border card-shadow p-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">Teams & Groups</h2>
            {teamsByGroup.length === 0 ? (
              <p className="text-muted-foreground text-sm">No teams have joined this tournament yet.</p>
            ) : (
              <div className="space-y-6">
                {teamsByGroup.map(({ group, teams }) => (
                  <div key={group.id}>
                    <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Icon name="Layers" size={14} className="text-muted-foreground" />
                      {group.name}
                    </h3>
                    {teams.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No teams assigned to this group.</p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {teams.map((tt) => (
                          <div key={tt.id} className="flex items-center gap-3 p-3 rounded-lg border border-border">
                            <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center overflow-hidden shrink-0">
                              {tt?.team?.logoUrl ? (
                                <Image src={tt.team.logoUrl} alt={tt.team.teamName} className="w-full h-full object-cover" />
                              ) : (
                                <Icon name="Shield" size={18} className="text-muted-foreground" />
                              )}
                            </div>
                            <p className="font-medium text-foreground truncate min-w-0">{tt?.team?.teamName || 'Unnamed Team'}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </PublicBrowseShell>
  );
};

export default TournamentDetailPage;
